import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Settings as SettingsIcon, Zap, Loader2, ShieldCheck, AlertCircle, Coins, Receipt, TrendingUp, ArrowUpRight, ArrowDownRight, Package, Download, Mail, Users, Search, ShieldAlert, Ban, ShieldX, MailCheck, BadgeCheck, Copy } from "lucide-react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid } from "recharts";
import { TeamPanel } from "@/components/TeamPanel";

const PACK_OPTIONS = [
    { key: "credit_pack_100", label: "100 credits · $10" },
    { key: "credit_pack_500", label: "500 credits · $40 (best value)" },
];

export default function Settings() {
    const { user } = useAuth();
    const [ar, setAr] = useState(null);
    const [usage, setUsage] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [txns, setTxns] = useState([]);
    const [history, setHistory] = useState(null);
    const [pressDownloading, setPressDownloading] = useState(false);
    const [emailTestSending, setEmailTestSending] = useState(false);
    const [emailTestResult, setEmailTestResult] = useState(null);
    const [signups, setSignups] = useState(null);
    const [signupsLoading, setSignupsLoading] = useState(false);
    const [signupsQuery, setSignupsQuery] = useState("");
    const [attempts, setAttempts] = useState(null);
    const [attemptsLoading, setAttemptsLoading] = useState(false);
    const [blocked, setBlocked] = useState(null);
    const [blockingIp, setBlockingIp] = useState("");
    const [upgrades, setUpgrades] = useState(null);
    const [upgradesLoading, setUpgradesLoading] = useState(false);
    const [approveBusyId, setApproveBusyId] = useState("");

    const loadAttempts = async () => {
        setAttemptsLoading(true);
        try {
            const [aRes, bRes] = await Promise.all([
                client.get("/admin/login-attempts"),
                client.get("/admin/blocked-ips"),
            ]);
            setAttempts(aRes.data);
            setBlocked(bRes.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load login attempts");
        } finally {
            setAttemptsLoading(false);
        }
    };

    const blockIp = async (ip, reason) => {
        setBlockingIp(ip);
        try {
            await client.post("/admin/blocked-ips", { ip, ttl_hours: 24, reason: reason || "bruteforce" });
            toast.success(`Blocked ${ip} for 24h`);
            await loadAttempts();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't block IP");
        } finally {
            setBlockingIp("");
        }
    };

    const unblockIp = async (ip) => {
        setBlockingIp(ip);
        try {
            await client.delete(`/admin/blocked-ips/${encodeURIComponent(ip)}`);
            toast.success(`Unblocked ${ip}`);
            await loadAttempts();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't unblock IP");
        } finally {
            setBlockingIp("");
        }
    };

    const loadSignups = async () => {
        setSignupsLoading(true);
        try {
            const { data } = await client.get("/admin/users");
            setSignups(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load signups");
        } finally {
            setSignupsLoading(false);
        }
    };

    const loadUpgrades = async () => {
        setUpgradesLoading(true);
        try {
            const { data } = await client.get("/admin/upgrades");
            setUpgrades(data.upgrades || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load upgrades");
        } finally {
            setUpgradesLoading(false);
        }
    };

    const approveUpgrade = async (u) => {
        setApproveBusyId(u.id);
        try {
            await client.post(`/admin/upgrades/${u.id}/approve`);
            toast.success(`Acknowledged ${u.email}'s upgrade`);
            await loadUpgrades();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't approve");
        } finally {
            setApproveBusyId("");
        }
    };

    const [verifyBusyId, setVerifyBusyId] = useState("");

    const resendVerify = async (u) => {
        setVerifyBusyId(u.id);
        try {
            const { data } = await client.post(`/admin/users/${u.id}/resend-verification`);
            if (data.status === "sent") {
                toast.success(`Verification email re-sent to ${u.email}`);
            } else if (data.status === "already_verified") {
                toast.info(`${u.email} is already verified — refreshing`);
            } else {
                // Failed to send — offer the link so admin can hand-deliver
                try {
                    await navigator.clipboard.writeText(data.verify_url || "");
                    toast.warning(
                        `Send failed (${data.error || "unknown"}). Verify link copied to clipboard — DM it to ${u.email}.`,
                        { duration: 12000 },
                    );
                } catch {
                    toast.error(`Send failed: ${data.error || "unknown"} · Link: ${data.verify_url}`);
                }
            }
            await loadSignups();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't resend verification");
        } finally {
            setVerifyBusyId("");
        }
    };

    const markVerified = async (u) => {
        if (!window.confirm(`Bypass email verification for ${u.email}?\n\nThis flips email_verified=true without them clicking the link. Use only when their ESP is rejecting our sender.`)) return;
        setVerifyBusyId(u.id);
        try {
            await client.post(`/admin/users/${u.id}/mark-verified`);
            toast.success(`${u.email} manually verified`);
            await loadSignups();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't mark verified");
        } finally {
            setVerifyBusyId("");
        }
    };

    const [creditBusyId, setCreditBusyId] = useState("");

    const grantCredits = async (u) => {
        const raw = window.prompt(
            `Grant credits to ${u.email}\n\nCurrent topup balance: ${u.credit_balance || 0} credits.\nEnter a positive number to add, negative to deduct:`,
            "100",
        );
        if (raw === null) return;
        const amount = parseInt(raw, 10);
        if (!amount || Number.isNaN(amount)) {
            toast.error("Enter a non-zero whole number");
            return;
        }
        setCreditBusyId(u.id);
        try {
            const { data } = await client.post(`/admin/users/${u.id}/grant-credits`, { credits: amount });
            toast.success(`${amount > 0 ? "+" : ""}${amount} credits → ${u.email} (balance: ${data.credit_balance})`);
            await loadSignups();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't grant credits");
        } finally {
            setCreditBusyId("");
        }
    };

    const exportSignupsCsv = () => {
        const rows = (signups?.users || []).filter((u) => {
            const q = signupsQuery.trim().toLowerCase();
            if (!q) return true;
            return (
                (u.email || "").toLowerCase().includes(q) ||
                (u.name || "").toLowerCase().includes(q) ||
                (u.tier || "").toLowerCase().includes(q)
            );
        });
        if (rows.length === 0) {
            toast.warning("Nothing to export — current filter has 0 rows.");
            return;
        }
        const headers = [
            "signed_up_at", "name", "email", "tier", "is_admin",
            "projects_count", "credit_months", "referred_by", "referral_code",
            "onboarded", "user_id",
        ];
        const esc = (v) => {
            if (v === null || v === undefined) return "";
            let s = String(v);
            // SEC: CSV formula injection guard. Prefix a single-quote to any
            // value starting with =, +, -, @, tab, or CR so spreadsheet apps
            // treat it as literal text instead of executing a formula.
            if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
            // CSV-escape: wrap in quotes if it contains comma, quote, or newline
            return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const lines = [headers.join(",")];
        for (const u of rows) {
            lines.push([
                u.created_at || "",
                u.name || "",
                u.email || "",
                u.is_admin ? "admin" : (u.tier || "free"),
                u.is_admin ? "true" : "false",
                u.projects_count ?? 0,
                u.credit_months ?? 0,
                u.referred_by || "",
                u.referral_code || "",
                u.onboarded ? "true" : "false",
                u.id || "",
            ].map(esc).join(","));
        }
        // Prepend UTF-8 BOM so Excel recognises Unicode properly
        const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        const a = document.createElement("a");
        a.href = url;
        a.download = `rankforge-signups-${stamp}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${rows.length} signup${rows.length === 1 ? "" : "s"} to CSV`);
    };

    const sendEmailTest = async () => {
        setEmailTestSending(true);
        setEmailTestResult(null);
        try {
            const { data } = await client.post("/admin/email-test", {});
            setEmailTestResult(data);
            if (data.status === "sent") {
                toast.success(`Real email dispatched to ${data.sent_to}`);
            } else if (data.status === "mocked") {
                toast.warning("Email is MOCKED — set RESEND_API_KEY in production .env");
            } else {
                toast.error(data.error || "Email send failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not run email test");
        } finally {
            setEmailTestSending(false);
        }
    };

    const downloadPressKit = async () => {
        setPressDownloading(true);
        try {
            const r = await client.get("/press-kit", { responseType: "blob" });
            const blob = new Blob([r.data], { type: "application/zip" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            a.href = url;
            a.download = `rankforge-press-kit-${stamp}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Press kit downloaded — check your Downloads folder.");
        } catch (err) {
            toast.error("Could not build press kit. Please retry.");
        } finally {
            setPressDownloading(false);
        }
    };

    useEffect(() => {
        Promise.all([
            client.get("/billing/auto-refill").then((r) => setAr(r.data)),
            client.get("/usage/me").then((r) => setUsage(r.data)),
            client.get("/billing/transactions?limit=50").then((r) => setTxns(r.data.transactions || [])),
            client.get("/billing/usage-history?months=6").then((r) => setHistory(r.data)),
        ])
            .catch(() => { /* ignore */ })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (user?.is_admin) {
            loadSignups();
            loadAttempts();
            loadUpgrades();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.is_admin]);

    const save = async () => {
        setSaving(true);
        try {
            await client.put("/billing/auto-refill", {
                enabled: ar.enabled,
                threshold: Number(ar.threshold),
                pack_lookup: ar.pack_lookup,
            });
            toast.success("Auto-refill settings saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading || !ar) {
        return <div className="text-muted-foreground font-mono text-sm" data-testid="settings-loading">loading settings…</div>;
    }

    const c = usage?.credits || {};

    return (
        <div className="space-y-8 animate-fade-in max-w-3xl" data-testid="settings-page">
            <div>
                <div className="overline">Account</div>
                <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-2">Settings</h1>
                <p className="text-sm text-muted-foreground mt-2">Billing controls, auto-refill, and credit balance.</p>
            </div>

            {/* Current balance card */}
            <div className="border border-border bg-card/40 p-6" data-testid="credit-balance-card">
                <div className="overline flex items-center gap-2"><Coins className="w-3 h-3" /> CURRENT BALANCE</div>
                <div className="mt-3 grid grid-cols-3 gap-4">
                    <div className="border border-border p-4">
                        <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Monthly used</div>
                        <div className="font-mono text-2xl mt-1">{c.monthly_used ?? 0}<span className="text-xs text-muted-foreground">/{c.monthly_limit ?? 0}</span></div>
                    </div>
                    <div className="border border-border p-4">
                        <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Pack balance</div>
                        <div className="font-mono text-2xl mt-1 text-primary">{c.topup_balance ?? 0}</div>
                    </div>
                    <div className="border border-border p-4">
                        <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Available</div>
                        <div className="font-mono text-2xl mt-1 text-success">{c.total_available ?? 0}</div>
                    </div>
                </div>
                <div className="mt-4 flex items-center gap-3">
                    <Link to="/pricing" className="text-xs text-primary hover:underline">Manage plan →</Link>
                </div>
            </div>

            {/* Auto-refill card */}
            <div className="border border-border bg-card/40 p-6" data-testid="auto-refill-card">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="overline flex items-center gap-2"><Zap className="w-3 h-3" /> AUTO-REFILL CREDITS</div>
                        <div className="font-display text-xl mt-1">Never hit a hard wall mid-workflow.</div>
                        <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                            When your total available credits fall below a threshold, we'll charge your card on file for a credit pack automatically. No interruption, no manual intervention.
                        </p>
                    </div>
                    {ar.available ? (
                        <span className="inline-flex items-center gap-1 border border-success/40 text-success bg-success/5 text-[10px] font-mono uppercase tracking-widest px-2 py-1">
                            <ShieldCheck className="w-3 h-3" /> AGENCY UNLOCKED
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 border border-warning/40 text-warning bg-warning/5 text-[10px] font-mono uppercase tracking-widest px-2 py-1">
                            <AlertCircle className="w-3 h-3" /> AGENCY REQUIRED
                        </span>
                    )}
                </div>

                <div className={`mt-6 space-y-5 ${!ar.available ? "opacity-50 pointer-events-none" : ""}`}>
                    {/* Toggle */}
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="auto-refill-toggle">
                        <div className={`relative w-11 h-6 border transition-colors ${ar.enabled ? "bg-primary border-primary" : "bg-input border-border"}`}
                             onClick={() => setAr({ ...ar, enabled: !ar.enabled })}>
                            <div className={`absolute top-0.5 w-4 h-4 bg-background transition-transform ${ar.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
                        </div>
                        <span className="text-sm">
                            {ar.enabled ? <span className="text-foreground">Enabled</span> : <span className="text-muted-foreground">Disabled</span>}
                        </span>
                    </label>

                    {/* Threshold + pack */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Refill when total credits drop below</label>
                            <div className="mt-2 flex items-center gap-3">
                                <input
                                    data-testid="auto-refill-threshold"
                                    type="number"
                                    min={0}
                                    max={5000}
                                    step={10}
                                    value={ar.threshold}
                                    onChange={(e) => setAr({ ...ar, threshold: Number(e.target.value) })}
                                    className="bg-input border border-border px-3 py-2 text-sm font-mono w-32 focus:outline-none focus:border-primary"
                                />
                                <span className="text-xs text-muted-foreground">credits</span>
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Auto-buy this pack</label>
                            <select
                                data-testid="auto-refill-pack"
                                value={ar.pack_lookup}
                                onChange={(e) => setAr({ ...ar, pack_lookup: e.target.value })}
                                className="mt-2 bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary w-full"
                            >
                                {PACK_OPTIONS.map((p) => (
                                    <option key={p.key} value={p.key}>{p.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Status */}
                    {ar.last_charged_at && (
                        <div className="text-xs text-muted-foreground border-l-2 border-success/40 pl-3">
                            Last auto-refill: <span className="font-mono">{new Date(ar.last_charged_at).toLocaleString()}</span>
                        </div>
                    )}
                    {ar.last_error && (
                        <div className="text-xs text-destructive border-l-2 border-destructive/40 pl-3" data-testid="auto-refill-error">
                            Last error: {ar.last_error}
                        </div>
                    )}

                    {/* Save button */}
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="auto-refill-save"
                            onClick={save}
                            disabled={saving}
                            className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60"
                        >
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            Save settings
                        </button>
                        <div className="text-xs text-muted-foreground">
                            Uses your default Stripe card. Manage in <Link to="/app" className="text-primary hover:underline">Manage billing</Link>.
                        </div>
                    </div>
                </div>

                {!ar.available && (
                    <div className="mt-6 border border-warning/40 bg-warning/5 p-4 text-sm" data-testid="auto-refill-locked">
                        Auto-refill is an <span className="text-warning font-medium">Agency-tier</span> feature.{" "}
                        <Link to="/pricing" className="text-primary hover:underline">Upgrade to enable →</Link>
                    </div>
                )}
            </div>

            {/* Team management */}
            <TeamPanel />

            {/* Usage graph */}
            {history && history.series?.length > 0 && (
                <div className="border border-border bg-card/40 p-6" data-testid="usage-history-card">
                    <div className="overline flex items-center gap-2 mb-3"><TrendingUp className="w-3 h-3" /> USAGE · LAST 6 MONTHS</div>
                    <div className="h-52">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={history.series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="creditFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#38CFFF" stopOpacity={0.4} />
                                        <stop offset="100%" stopColor="#38CFFF" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="period" stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} />
                                <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} />
                                <Tooltip
                                    contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "monospace", fontSize: 12 }}
                                    labelStyle={{ color: "#9ca3af" }}
                                />
                                {history.monthly_limit > 0 && (
                                    <ReferenceLine y={history.monthly_limit} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: `Limit ${history.monthly_limit}`, position: "insideTopRight", fill: "#f59e0b", fontSize: 10 }} />
                                )}
                                <Area type="monotone" dataKey="credits_used" stroke="#38CFFF" strokeWidth={2} fill="url(#creditFill)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Press kit */}
            <div className="border border-border bg-card/40 p-6" data-testid="press-kit-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="max-w-2xl">
                        <div className="overline flex items-center gap-2"><Package className="w-3 h-3" /> PRESS KIT</div>
                        <h2 className="font-display text-2xl tracking-tight mt-2">
                            Download the full media pack
                        </h2>
                        <p className="text-sm text-muted-foreground mt-2">
                            One <span className="font-mono text-foreground">.zip</span> containing everything you need to write about, review,
                            or share InPhase and Phase 2 Labs Marketing. One folder per brand.
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4 text-[11px] font-mono text-muted-foreground">
                            <div>▸ Logos (PNG + SVG)</div>
                            <div>▸ Brand.md (colors, fonts, voice)</div>
                            <div>▸ One-pager PDF</div>
                            <div>▸ Hero card 1200×630</div>
                            <div>▸ Email launch template</div>
                            <div>▸ X / LinkedIn / FB / IG / Threads copy</div>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <button
                            onClick={downloadPressKit}
                            disabled={pressDownloading}
                            data-testid="download-press-kit-btn"
                            className="bg-primary text-primary-foreground px-5 py-3 text-sm font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60"
                        >
                            {pressDownloading ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Preparing…</>
                            ) : (
                                <><Download className="w-4 h-4" /> Download press kit (.zip)</>
                            )}
                        </button>
                        <span className="text-[10px] font-mono text-muted-foreground/70">
                            Rebuilt fresh on every download · ~400 KB
                        </span>
                    </div>
                </div>
            </div>

            {/* Admin-only: Email delivery test */}
            {user?.is_admin && (
                <div className="border border-border bg-card/40 p-6" data-testid="email-test-card">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="max-w-2xl">
                            <div className="overline flex items-center gap-2">
                                <Mail className="w-3 h-3" /> EMAIL DELIVERY · ADMIN
                            </div>
                            <h2 className="font-display text-2xl tracking-tight mt-2">
                                Fire a real Resend test to your inbox
                            </h2>
                            <p className="text-sm text-muted-foreground mt-2">
                                Sends a branded email to <span className="font-mono text-foreground">{user?.email}</span> via
                                the exact same transport as password resets, budget alerts, and share reports.
                                Use this after flipping <span className="font-mono">RESEND_API_KEY</span> in production to
                                confirm the key + verified sending domain are actually delivering.
                            </p>
                            {emailTestResult && (
                                <div
                                    data-testid="email-test-result"
                                    className={`mt-4 border p-3 text-xs font-mono ${
                                        emailTestResult.status === "sent"
                                            ? "border-primary/40 bg-primary/5 text-foreground"
                                            : emailTestResult.status === "mocked"
                                                ? "border-yellow-500/40 bg-yellow-500/5 text-yellow-200"
                                                : "border-destructive/40 bg-destructive/5 text-destructive"
                                    }`}
                                >
                                    <div>status: <strong>{emailTestResult.status}</strong></div>
                                    <div>live: {String(emailTestResult.live)}</div>
                                    <div>to: {emailTestResult.sent_to}</div>
                                    {emailTestResult.message_id && (
                                        <div>message_id: {emailTestResult.message_id}</div>
                                    )}
                                    {emailTestResult.error && (
                                        <div className="text-destructive">error: {emailTestResult.error}</div>
                                    )}
                                    {emailTestResult.status === "mocked" && (
                                        <div className="mt-2 text-yellow-300/80">
                                            → Set <span className="font-bold">RESEND_API_KEY</span> in production env vars.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <button
                                onClick={sendEmailTest}
                                disabled={emailTestSending}
                                data-testid="send-email-test-btn"
                                className="bg-primary text-primary-foreground px-5 py-3 text-sm font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60"
                            >
                                {emailTestSending ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                                ) : (
                                    <><Mail className="w-4 h-4" /> Send test email</>
                                )}
                            </button>
                            <span className="text-[10px] font-mono text-muted-foreground/70">
                                Admin-only · lands in your own inbox
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Change Password (available to every logged-in user) */}
            <ChangePasswordCard />

            {/* Admin-only: Upgrade approvals (FYI, non-blocking) */}
            {user?.is_admin && (
                <div className="border border-border bg-card/40 p-6" data-testid="upgrade-approvals-card">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="overline flex items-center gap-2">
                                <ArrowUpRight className="w-3 h-3" /> UPGRADES · ADMIN
                            </div>
                            <h2 className="font-display text-2xl tracking-tight mt-2">
                                Paid upgrade notifications
                            </h2>
                            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                                FYI only — upgrades apply immediately via Stripe. Acknowledge here to clear the review flag, or use the one-click link emailed to {" "}
                                <span className="font-mono text-foreground">phase2labs@gmail.com</span>.
                            </p>
                        </div>
                        <button
                            onClick={loadUpgrades}
                            disabled={upgradesLoading}
                            data-testid="refresh-upgrades-btn"
                            className="border border-border px-3 py-2 text-xs font-mono uppercase tracking-widest hover:border-primary hover:text-primary disabled:opacity-60 flex items-center gap-2"
                        >
                            {upgradesLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />}
                            Refresh
                        </button>
                    </div>

                    {upgradesLoading && !upgrades ? (
                        <div className="mt-4 text-sm text-muted-foreground font-mono" data-testid="upgrades-loading">loading upgrades…</div>
                    ) : upgrades?.length ? (
                        <div className="mt-4 border border-border max-h-[20rem] overflow-y-auto overflow-x-auto">
                            <table className="w-full text-xs" data-testid="upgrades-table">
                                <thead className="text-[10px] text-muted-foreground uppercase tracking-widest bg-card/60 sticky top-0">
                                    <tr>
                                        <th className="text-left p-2">When</th>
                                        <th className="text-left p-2">User</th>
                                        <th className="text-left p-2">Tier</th>
                                        <th className="text-right p-2">Amount</th>
                                        <th className="text-center p-2">Status</th>
                                        <th className="text-right p-2">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {upgrades.map((u) => (
                                        <tr key={u.id} className="border-t border-border/50 hover:bg-card/30" data-testid={`upgrade-row-${u.id}`}>
                                            <td className="p-2 font-mono text-muted-foreground whitespace-nowrap">
                                                {u.created_at ? new Date(u.created_at).toLocaleString(undefined, { year: "2-digit", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                                            </td>
                                            <td className="p-2 font-mono">{u.email}</td>
                                            <td className="p-2">
                                                <span className="inline-block text-[10px] font-mono px-1.5 py-0.5 border border-primary/60 text-primary">
                                                    {(u.tier || "").toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="p-2 text-right font-mono">{u.amount ? `${u.currency} ${u.amount.toFixed(2)}` : "—"}</td>
                                            <td className="p-2 text-center">
                                                {u.approved ? (
                                                    <BadgeCheck className="w-4 h-4 text-success inline" data-testid={`upgrade-approved-${u.id}`} />
                                                ) : (
                                                    <span className="inline-block text-[10px] font-mono px-1.5 py-0.5 border border-warning text-warning" data-testid={`upgrade-pending-${u.id}`}>PENDING</span>
                                                )}
                                            </td>
                                            <td className="p-2 text-right">
                                                {!u.approved && (
                                                    <button
                                                        data-testid={`approve-upgrade-btn-${u.id}`}
                                                        onClick={() => approveUpgrade(u)}
                                                        disabled={approveBusyId === u.id}
                                                        title="Acknowledge upgrade"
                                                        className="border border-border p-1 text-muted-foreground hover:border-success hover:text-success disabled:opacity-50"
                                                    >
                                                        {approveBusyId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <BadgeCheck className="w-3 h-3" />}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="mt-4 text-sm text-muted-foreground font-mono" data-testid="upgrades-empty">No paid upgrades yet.</div>
                    )}
                </div>
            )}

            {/* Admin-only: Signups directory */}
            {user?.is_admin && (
                <div className="border border-border bg-card/40 p-6" data-testid="admin-signups-card">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="overline flex items-center gap-2">
                                <Users className="w-3 h-3" /> SIGNUPS · ADMIN
                            </div>
                            <h2 className="font-display text-2xl tracking-tight mt-2">
                                Who has signed up
                            </h2>
                            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                                Every registered account, newest first. Includes tier, referral origin, and project count so you can eyeball the funnel at a glance.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={exportSignupsCsv}
                                disabled={!signups?.users?.length}
                                data-testid="export-signups-csv-btn"
                                className="border border-border px-3 py-2 text-xs font-mono uppercase tracking-widest hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                <Download className="w-3 h-3" />
                                Export CSV
                            </button>
                            <button
                                onClick={loadSignups}
                                disabled={signupsLoading}
                                data-testid="refresh-signups-btn"
                                className="border border-border px-3 py-2 text-xs font-mono uppercase tracking-widest hover:border-primary hover:text-primary disabled:opacity-60 flex items-center gap-2"
                            >
                                {signupsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />}
                                Refresh
                            </button>
                        </div>
                    </div>

                    {signups?.stats && (
                        <div className="mt-5 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="signups-stats">
                            {[
                                ["Total", signups.stats.total],
                                ["Paid", signups.stats.paid],
                                ["Admins", signups.stats.admins],
                                ["Referred", signups.stats.referred],
                                ["Last 7d", signups.stats.last_7_days],
                                ["Last 30d", signups.stats.last_30_days],
                            ].map(([k, v]) => (
                                <div key={k} className="border border-border p-3">
                                    <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{k}</div>
                                    <div className="font-mono text-xl mt-1">{v}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="mt-5 flex items-center gap-2 border border-border bg-input px-3">
                        <Search className="w-3 h-3 text-muted-foreground" />
                        <input
                            type="text"
                            value={signupsQuery}
                            onChange={(e) => setSignupsQuery(e.target.value)}
                            placeholder="filter by email, name, or tier…"
                            data-testid="signups-filter-input"
                            className="bg-transparent py-2 text-sm font-mono w-full focus:outline-none placeholder:text-muted-foreground/60"
                        />
                    </div>

                    {signupsLoading && !signups ? (
                        <div className="mt-4 text-sm text-muted-foreground font-mono" data-testid="signups-loading">loading signups…</div>
                    ) : signups?.users?.length ? (
                        <div className="mt-4 border border-border max-h-[28rem] overflow-y-auto overflow-x-auto">
                            <table className="w-full text-xs" data-testid="signups-table">
                                <thead className="text-[10px] text-muted-foreground uppercase tracking-widest bg-card/60 sticky top-0">
                                    <tr>
                                        <th className="text-left p-2">Signed up</th>
                                        <th className="text-left p-2">Name</th>
                                        <th className="text-left p-2">Email</th>
                                        <th className="text-left p-2">Tier</th>
                                        <th className="text-center p-2">Verified</th>
                                        <th className="text-right p-2">Projects</th>
                                        <th className="text-right p-2">Credits</th>
                                        <th className="text-left p-2">Referred by</th>
                                        <th className="text-right p-2">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {signups.users
                                        .filter((u) => {
                                            const q = signupsQuery.trim().toLowerCase();
                                            if (!q) return true;
                                            return (
                                                (u.email || "").toLowerCase().includes(q) ||
                                                (u.name || "").toLowerCase().includes(q) ||
                                                (u.tier || "").toLowerCase().includes(q)
                                            );
                                        })
                                        .map((u) => (
                                            <tr key={u.id} className="border-t border-border/50 hover:bg-card/30" data-testid={`signup-row-${u.id}`}>
                                                <td className="p-2 font-mono text-muted-foreground whitespace-nowrap">
                                                    {u.created_at ? new Date(u.created_at).toLocaleString(undefined, { year: "2-digit", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                                                </td>
                                                <td className="p-2">{u.name || <span className="text-muted-foreground/60">—</span>}</td>
                                                <td className="p-2 font-mono">{u.email}</td>
                                                <td className="p-2">
                                                    <span className={`inline-block text-[10px] font-mono px-1.5 py-0.5 border ${
                                                        u.is_admin ? "border-primary text-primary" :
                                                        u.tier === "agency" ? "border-primary text-primary" :
                                                        (u.tier === "solo" || u.tier === "pro") ? "border-primary/60 text-primary" :
                                                        "border-border text-muted-foreground"
                                                    }`}>
                                                        {u.is_admin ? "ADMIN" : (u.tier || "free").toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="p-2 text-center">
                                                    {u.email_verified ? (
                                                        <BadgeCheck className="w-4 h-4 text-success inline" data-testid={`verified-${u.id}`} />
                                                    ) : (
                                                        <span className="inline-block text-[10px] font-mono px-1.5 py-0.5 border border-warning text-warning" data-testid={`unverified-${u.id}`}>PENDING</span>
                                                    )}
                                                </td>
                                                <td className="p-2 text-right font-mono">{u.projects_count}</td>
                                                <td className="p-2 text-right font-mono" data-testid={`credit-balance-${u.id}`}>
                                                    {u.credit_balance > 0 ? <span className="text-primary">{u.credit_balance}</span> : <span className="text-muted-foreground/40">0</span>}
                                                </td>
                                                <td className="p-2 font-mono text-muted-foreground">
                                                    {u.referred_by || <span className="text-muted-foreground/40">—</span>}
                                                </td>
                                                <td className="p-2 text-right whitespace-nowrap">
                                                    <span className="inline-flex gap-1">
                                                        <button
                                                            data-testid={`grant-credits-btn-${u.id}`}
                                                            onClick={() => grantCredits(u)}
                                                            disabled={creditBusyId === u.id}
                                                            title="Grant extra credits"
                                                            className="border border-border p-1 text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-50"
                                                        >
                                                            {creditBusyId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Coins className="w-3 h-3" />}
                                                        </button>
                                                    {!u.email_verified && (
                                                        <span className="inline-flex gap-1">
                                                            <button
                                                                data-testid={`resend-verify-btn-${u.id}`}
                                                                onClick={() => resendVerify(u)}
                                                                disabled={verifyBusyId === u.id}
                                                                title="Resend verification email"
                                                                className="border border-border p-1 text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-50"
                                                            >
                                                                {verifyBusyId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <MailCheck className="w-3 h-3" />}
                                                            </button>
                                                            <button
                                                                data-testid={`mark-verified-btn-${u.id}`}
                                                                onClick={() => markVerified(u)}
                                                                disabled={verifyBusyId === u.id}
                                                                title="Manually mark verified (bypass email)"
                                                                className="border border-border p-1 text-muted-foreground hover:border-success hover:text-success disabled:opacity-50"
                                                            >
                                                                <BadgeCheck className="w-3 h-3" />
                                                            </button>
                                                        </span>
                                                    )}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="mt-4 border border-dashed border-border p-6 text-sm text-muted-foreground text-center" data-testid="signups-empty">
                            No signups yet.
                        </div>
                    )}
                </div>
            )}

            {/* Admin-only: Failed login attempts */}
            {user?.is_admin && (
                <div className="border border-border bg-card/40 p-6" data-testid="admin-login-attempts-card">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="overline flex items-center gap-2">
                                <ShieldAlert className="w-3 h-3" /> BRUTEFORCE WATCH · ADMIN
                            </div>
                            <h2 className="font-display text-2xl tracking-tight mt-2">
                                Recent failed logins by IP
                            </h2>
                            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                                Sliding 24h window. Grouped by source IP so ongoing credential-stuffing is obvious at a glance. Each IP is already rate-limited to 20 attempts per 10 minutes.
                            </p>
                        </div>
                        <button
                            onClick={loadAttempts}
                            disabled={attemptsLoading}
                            data-testid="refresh-attempts-btn"
                            className="border border-border px-3 py-2 text-xs font-mono uppercase tracking-widest hover:border-primary hover:text-primary disabled:opacity-60 flex items-center gap-2"
                        >
                            {attemptsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />}
                            Refresh
                        </button>
                    </div>

                    {attempts && (
                        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="attempts-stats">
                            {[
                                ["Failed (24h)", attempts.total_failed, attempts.total_failed > 0 ? "text-destructive" : ""],
                                ["Successful", attempts.total_success, ""],
                                ["Distinct IPs (failed)", attempts.distinct_ips, ""],
                                ["Window", `${attempts.window_hours}h`, "text-muted-foreground"],
                            ].map(([k, v, cls]) => (
                                <div key={k} className="border border-border p-3">
                                    <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{k}</div>
                                    <div className={`font-mono text-xl mt-1 ${cls}`}>{v}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {attemptsLoading && !attempts ? (
                        <div className="mt-4 text-sm text-muted-foreground font-mono" data-testid="attempts-loading">loading login activity…</div>
                    ) : attempts?.by_ip?.length ? (
                        <div className="mt-4 border border-border max-h-96 overflow-y-auto">
                            <table className="w-full text-xs" data-testid="attempts-table">
                                <thead className="text-[10px] text-muted-foreground uppercase tracking-widest bg-card/60 sticky top-0">
                                    <tr>
                                        <th className="text-left p-2">Source IP</th>
                                        <th className="text-right p-2">Failed</th>
                                        <th className="text-right p-2">Emails tried</th>
                                        <th className="text-left p-2">Last attempt</th>
                                        <th className="text-left p-2">Sample</th>
                                        <th className="text-right p-2">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {attempts.by_ip.map((r) => {
                                        const isBlocked = (blocked?.blocked || []).some((b) => b.ip === r.ip);
                                        const isBusy = blockingIp === r.ip;
                                        return (
                                        <tr key={r.ip} className="border-t border-border/50" data-testid={`attempt-row-${r.ip}`}>
                                            <td className="p-2 font-mono">{r.ip}</td>
                                            <td className={`p-2 text-right font-mono ${r.failed >= 10 ? "text-destructive" : r.failed >= 5 ? "text-warning" : ""}`}>{r.failed}</td>
                                            <td className="p-2 text-right font-mono">{r.unique_emails}</td>
                                            <td className="p-2 font-mono text-muted-foreground">
                                                {r.last_ts ? new Date(r.last_ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                                            </td>
                                            <td className="p-2 font-mono text-muted-foreground truncate max-w-xs">
                                                {(r.sample_emails || []).join(", ") || "—"}
                                            </td>
                                            <td className="p-2 text-right">
                                                {isBlocked ? (
                                                    <span
                                                        data-testid={`blocked-badge-${r.ip}`}
                                                        className="inline-flex items-center gap-1 border border-destructive/50 bg-destructive/10 text-destructive text-[10px] font-mono uppercase tracking-widest px-2 py-1"
                                                    >
                                                        <ShieldX className="w-3 h-3" /> BLOCKED
                                                    </span>
                                                ) : (
                                                    <button
                                                        data-testid={`block-ip-btn-${r.ip}`}
                                                        onClick={() => {
                                                            if (window.confirm(`Block ${r.ip} for 24 hours?\n\nAll requests from this IP will return 429. TTL auto-expires.`)) {
                                                                blockIp(r.ip, `bruteforce: ${r.failed} failed`);
                                                            }
                                                        }}
                                                        disabled={isBusy}
                                                        className="border border-destructive/50 text-destructive px-2 py-1 text-[10px] font-mono uppercase tracking-widest hover:bg-destructive/10 disabled:opacity-50 inline-flex items-center gap-1"
                                                    >
                                                        {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />}
                                                        Block 24h
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="mt-4 border border-dashed border-border p-6 text-sm text-muted-foreground text-center" data-testid="attempts-empty">
                            No failed logins in the last 24 hours. All quiet.
                        </div>
                    )}

                    {/* Currently Blocked IPs */}
                    {(blocked?.blocked || []).length > 0 && (
                        <div className="mt-5" data-testid="blocked-ips-section">
                            <div className="overline flex items-center gap-2 mb-2 text-destructive">
                                <ShieldX className="w-3 h-3" /> CURRENTLY BLOCKED · {blocked.count}
                            </div>
                            <div className="border border-destructive/30 max-h-56 overflow-y-auto">
                                <table className="w-full text-xs" data-testid="blocked-ips-table">
                                    <thead className="text-[10px] text-muted-foreground uppercase tracking-widest bg-card/60 sticky top-0">
                                        <tr>
                                            <th className="text-left p-2">IP</th>
                                            <th className="text-left p-2">Reason</th>
                                            <th className="text-left p-2">Blocked at</th>
                                            <th className="text-left p-2">Expires</th>
                                            <th className="text-right p-2">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {blocked.blocked.map((b) => (
                                            <tr key={b.ip} className="border-t border-border/50" data-testid={`blocked-row-${b.ip}`}>
                                                <td className="p-2 font-mono">{b.ip}</td>
                                                <td className="p-2 font-mono text-muted-foreground truncate max-w-xs">{b.reason || "—"}</td>
                                                <td className="p-2 font-mono text-muted-foreground">
                                                    {b.blocked_at ? new Date(b.blocked_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                                                </td>
                                                <td className="p-2 font-mono text-muted-foreground">
                                                    {b.expires_at ? new Date(b.expires_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                                                </td>
                                                <td className="p-2 text-right">
                                                    <button
                                                        data-testid={`unblock-ip-btn-${b.ip}`}
                                                        onClick={() => unblockIp(b.ip)}
                                                        disabled={blockingIp === b.ip}
                                                        className="border border-border px-2 py-1 text-[10px] font-mono uppercase tracking-widest hover:border-primary hover:text-primary disabled:opacity-50 inline-flex items-center gap-1"
                                                    >
                                                        {blockingIp === b.ip ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                                        Unblock
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Transactions ledger */}
            <div className="border border-border bg-card/40 p-6" data-testid="transactions-card">
                <div className="flex items-center justify-between mb-4">
                    <div className="overline flex items-center gap-2"><Receipt className="w-3 h-3" /> TRANSACTIONS <span className="font-mono text-muted-foreground">({txns.length})</span></div>
                </div>
                {txns.length === 0 ? (
                    <div className="border border-dashed border-border p-6 text-sm text-muted-foreground text-center" data-testid="txns-empty">
                        No transactions yet. Every credit spent, pack purchased, or auto-refill will show up here.
                    </div>
                ) : (
                    <div className="border border-border max-h-96 overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="text-[10px] text-muted-foreground uppercase tracking-widest bg-card/60 sticky top-0">
                                <tr>
                                    <th className="text-left p-2">When</th>
                                    <th className="text-left p-2">Description</th>
                                    <th className="text-left p-2">Source</th>
                                    <th className="text-right p-2">Credits</th>
                                </tr>
                            </thead>
                            <tbody>
                                {txns.map((t) => (
                                    <tr key={t.id} className="border-t border-border/50" data-testid={`txn-row-${t.id}`}>
                                        <td className="p-2 font-mono text-muted-foreground">
                                            {new Date(t.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                        </td>
                                        <td className="p-2">{t.description}</td>
                                        <td className="p-2 font-mono text-muted-foreground">{t.source}</td>
                                        <td className={`p-2 text-right font-mono ${t.credits_delta > 0 ? "text-success" : "text-destructive"}`}>
                                            <span className="inline-flex items-center gap-0.5 justify-end">
                                                {t.credits_delta > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                                                {t.credits_delta > 0 ? "+" : ""}{t.credits_delta}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}


function ChangePasswordCard() {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 8) {
            toast.error("New password must be at least 8 characters");
            return;
        }
        if (next !== confirm) {
            toast.error("New password and confirmation don't match");
            return;
        }
        if (next === current) {
            toast.error("New password must be different from your current one");
            return;
        }
        setBusy(true);
        try {
            await client.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password updated — use it next time you sign in");
            setCurrent("");
            setNext("");
            setConfirm("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't change password");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="border border-border bg-card/40 p-6" data-testid="change-password-card">
            <div className="overline flex items-center gap-2">
                <ShieldCheck className="w-3 h-3" /> ACCOUNT SECURITY
            </div>
            <h2 className="font-display text-2xl tracking-tight mt-2">Change password</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                Rotate your login password without going through the email-reset flow.
                Requires your current password — mitigates session-hijack risk.
            </p>

            <form onSubmit={submit} className="mt-5 grid gap-4 max-w-md">
                <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Current password</label>
                    <input
                        type="password"
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                        required
                        autoComplete="current-password"
                        data-testid="change-pw-current"
                        className="mt-1 w-full bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
                    />
                </div>
                <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-widest">New password <span className="text-muted-foreground/60">(min 8 chars)</span></label>
                    <input
                        type="password"
                        value={next}
                        onChange={(e) => setNext(e.target.value)}
                        required
                        minLength={8}
                        autoComplete="new-password"
                        data-testid="change-pw-new"
                        className="mt-1 w-full bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
                    />
                </div>
                <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Confirm new password</label>
                    <input
                        type="password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        required
                        minLength={8}
                        autoComplete="new-password"
                        data-testid="change-pw-confirm"
                        className={`mt-1 w-full bg-input border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary ${confirm && confirm !== next ? "border-destructive" : "border-border"}`}
                    />
                    {confirm && confirm !== next && (
                        <div className="text-xs text-destructive mt-1 font-mono" data-testid="change-pw-mismatch">passwords don't match</div>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="submit"
                        disabled={busy || !current || !next || next !== confirm}
                        data-testid="change-pw-submit"
                        className="border border-primary bg-primary/10 text-primary px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-primary hover:text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                        Update password
                    </button>
                    <span className="text-xs text-muted-foreground font-mono">
                        You stay logged in on this session.
                    </span>
                </div>
            </form>
        </div>
    );
}

